import React, { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";

import { getBaseUnits, getUnitOptions } from "utils/fields";
import { getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { provinceCodeMap } from "utils/weather";
import { useStateSelector } from "utils/field";
import {
    getElectricVehicleOptionsTemplate,
    getChargeProgramTemplate,
    getChargePreferenceTemplate,
    getChargePreferenceOptions,
    getChargeProgramOptions,
    getElectricVehicleSpecs,
    getVehicleYearOptions,
    getVehicleManufacturerOptions,
    getVehicleModelOptions,
} from "utils/transportation";

import Field from "components/Field";
import Checkbox from "components/Input/Checkbox";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Accordion from "components/Accordion";
import Actions from "../Actions";
import Button from "components/Button";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

// Validations
const commuteDistanceValidation = getValidation("commuteDistance");
const motorValidation = getValidation("motor");
const emissionsValidation = getValidation("emissionsRate");
const combinedFuelEconomyValidation = getValidation("electricFuelEconomy");
const combinedFuelEconomyGasEquivalentValidation = getValidation("electricFuelEconomyGasEquivalent");
const stateOfChargeValidation = getValidation("stateOfCharge");
const chargerValidation = getValidation("chargerSettings");
const roundTripEfficiencyValidation = getValidation("roundTripEfficiency");
const loadThresholdValidation = getValidation("loadThreshold");
const time24hValidation = getValidation("time24h");

// Helper Functions
function resetValues(change, accessor) {
    change(`${accessor}.vehicleClass`, "");
    change(`${accessor}.motor`, "");
    change(`${accessor}.transmission`, "");
    change(`${accessor}.emissions`, "");
    change(`${accessor}.emissionRating`, "");
    change(`${accessor}.smogRating`, "");
    change(`${accessor}.fuelType`, "");
    change(`${accessor}.combinedFuelEconomy`, "");
    change(`${accessor}.combinedFuelEconomyGasEquivalent`, "");
    change(`${accessor}.rechargeTime`, "");
    change(`${accessor}.range`, "");
}

function modelToolTip(model) {
    switch (true) {
        case model.includes("(Low)"):
            return "Performance is calculated annually based on fuel economy (kWh/100km) and vehicle class. A 'Low' rating means the vehicle was in the 25th percentile for performance for the year based on these factors.";

        case model.includes("(Mid)"):
            return "Performance is calculated annually based on fuel economy (kWh/100km) and vehicle class. A 'Mid' rating means the vehicle was in the 50th percentile for performance for the year based on these factors.";

        case model.includes("(High)"):
            return "Performance is calculated annually based on fuel economy (kWh/100km) and vehicle class. A 'High' rating means the vehicle was in the 75th percentile for performance for the year based on these factors.";

        default:
            return "Performance is calculated annually based on fuel economy (kWh/100km) and vehicle class.";
    }
}

export default ({ accessor, electricityPrice, change, formName, forceOpen: forceOpenProp = false }) => {
    const [specs, setSpecs] = useState({
        fuelTypeInfo: null,
        vehicleClassInfo: null,
        transmissionInfo: null,
        annualDistanceDriven: null,
        annualEmissions: null,
        annualFuelConsumption: null,
        annualFuelCost: null,
    });
    const [isLoading, setIsLoading] = useState(false);

    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const contentOpen = useStateSelector(`model.contentOpen`) || false;

    // Unit Assignment
    const commuteDistanceUnits = useStateSelector(`${accessor}.commuteDistance_u`);
    const distanceUnits = useStateSelector(`${accessor}.distance_u`);
    const motorUnits = useStateSelector(`${accessor}.motor_u`);
    const emissionsRateUnits = useStateSelector(`${accessor}.emissionsRate_u`);
    const emissionsUnits = useStateSelector(`${accessor}.emissions_u`);
    const electricFuelEconomyUnits = useStateSelector(`${accessor}.electricFuelEconomy_u`);
    const electricFuelEconomyGasEquivalentUnits = useStateSelector(`${accessor}.electricFuelEconomyGasEquivalent_u`);
    const electricityConsumptionUnits = useStateSelector(`${accessor}.electricityConsumption_u`);
    const stateOfChargeUnits = useStateSelector(`${accessor}.stateOfCharge_u`);
    const priceUnits = useStateSelector(`${accessor}.price_u`);
    const timeOfUsePriceUnits = useStateSelector(`${accessor}.timeOfUsePrice_u`);
    const efficiencyUnits = useStateSelector(`${accessor}.efficiency_u`);
    const loadThresholdUnits = useStateSelector(`${accessor}.loadThreshold_u`);
    const chargerUnits = useStateSelector(`${accessor}.charger_u`);

    // Form Value Assignment
    const commuteDistance = useStateSelector(`${accessor}.commuteDistance`) || 0;
    const sysNum = useStateSelector(`${accessor}.systemNumber`) || 1;
    const vehicleYear = useStateSelector(`${accessor}.vehicleYear`) || "";
    const manufacturer = useStateSelector(`${accessor}.manufacturer`) || "";
    const model = useStateSelector(`${accessor}.model`) || "";
    const vehicleClass = useStateSelector(`${accessor}.vehicleClass`) || "";
    const motor = useStateSelector(`${accessor}.motor`) || "";
    const transmission = useStateSelector(`${accessor}.transmission`) || "";
    const emissions = useStateSelector(`${accessor}.emissions`) || "";
    const emissionRating = useStateSelector(`${accessor}.emissionRating`) || "";
    const smogRating = useStateSelector(`${accessor}.smogRating`) || "";
    const fuelType = useStateSelector(`${accessor}.fuelType`) || "";
    const combinedFuelEconomy = useStateSelector(`${accessor}.combinedFuelEconomy`) || "";
    const combinedFuelEconomyGasEquivalent = useStateSelector(`${accessor}.combinedFuelEconomyGasEquivalent`) || "";
    const rechargeTime = useStateSelector(`${accessor}.rechargeTime`) || "";
    const range = useStateSelector(`${accessor}.range`) || "";
    const maxSoc = useStateSelector(`${accessor}.maxSoc`) || 90;
    const minSoc = useStateSelector(`${accessor}.minSoc`) || 20;
    const roundTripEfficiency = useStateSelector(`${accessor}.roundTripEfficiency`) || 90;
    const loadThreshold = useStateSelector(`${accessor}.loadThreshold`) || 10; // in kW, used for load-aware chargePreference
    const chargerCapacity = useStateSelector(`${accessor}.chargerCapacity`) || 6.6; // assuming symmetrical charging/discharging (therefore, assumed AC and DC bidirectional charging)
    const startSoc = useStateSelector(`${accessor}.startSoc`) || 50;
    const chargingHomeHourStart = useStateSelector(`${accessor}.chargingHomeHourStart`) || 18;
    const chargingHomeHourEnd = useStateSelector(`${accessor}.chargingHomeHourEnd`) || 8;
    const workHourStart = useStateSelector(`${accessor}.workHourStart`) || 9;
    const workHourEnd = useStateSelector(`${accessor}.workHourEnd`) || 17;
    const v2gHourStart = useStateSelector(`${accessor}.v2gHourStart`) || 18;
    const v2gHourEnd = useStateSelector(`${accessor}.v2gHourEnd`) || 8;
    const chargeProgram = useStateSelector(`${accessor}.chargeProgram`) || "None";
    const chargePreference = useStateSelector(`${accessor}.chargePreference`) || "Uncontrolled";
    const evProfile = useStateSelector(`${accessor}.evProfile`) || {};
    const enableV2G = useStateSelector(`${accessor}.enableV2G`) || false;
    const enableTOU = useStateSelector(`${accessor}.enableTOU`) || false;
    const weekdayScheduleTOU =
        useStateSelector("model.modelData.general.timeOfUse.weekdayScheduleTOU") ||
        Array.from({ length: 12 }, () => Array(24).fill(0));
    const weekendScheduleTOU =
        useStateSelector(`model.modelData.general.timeOfUse.weekendScheduleTOU`) ||
        Array.from({ length: 12 }, () => Array(24).fill(0));
    const numberOfPeriods = useStateSelector(`model.modelData.general.timeOfUse.numberOfPeriods`) || 1;
    const periodPrices =
        useStateSelector(`model.modelData.general.timeOfUse.periodPrices`) ||
        Array.from({ length: numberOfPeriods }).fill(0);
    const arbitrageBuyPrice = useStateSelector(`${accessor}.arbitrageBuyPrice`) || 0; // if no price is set, use the min of the period prices as a default
    const arbitrageSellPrice = useStateSelector(`${accessor}.arbitrageSellPrice`) || 0; // if no price is set, use the max of the period prices as a default
    const { id: locationId } = useStateSelector("model.modelData.location.weather.region") || {};

    //const { packageResults = {} } = useStateSelector("results") || {}; // DEBUG
    //const houseLoadProfile =
    //    packageResults?.base?.resultsData?.generalBaseCase?.hourlyElectricalLoadkWh || [];

    function containsZero(nestedDict) {
        for (const outerKey in nestedDict) {
            for (const innerKey in nestedDict[outerKey]) {
                if (nestedDict[outerKey][innerKey] === 0) {
                    return true; // found 0
                }
            }
        }
        return false; // no 0 found
    }

    // Option Retrieval
    const vehicleMap = getElectricVehicleOptionsTemplate(); //get all electric-vehicle data options
    const chargeProgramMap = getChargeProgramTemplate();
    const chargePreferenceMap = getChargePreferenceTemplate();

    const yearOpts = getVehicleYearOptions(vehicleMap);
    const manufacturerOpts = getVehicleManufacturerOptions(vehicleYear, vehicleMap);
    const modelOpts = getVehicleModelOptions(vehicleYear, manufacturer, vehicleMap);
    //const chargeProgramOpts = getChargeProgramOptions(chargeProgramMap).filter(({ value }) => value !== "Arbitrage");
    const chargeProgramOpts = getChargeProgramOptions(chargeProgramMap);
    const chargePreferenceOpts = getChargePreferenceOptions(chargePreferenceMap);

    const handleButton = () => {
        setIsLoading(true);
        // Delay the synchronous work to allow loading state to show
        setTimeout(() => {
            const results = getElectricVehicleSpecs(
                commuteDistance,
                {
                    vehicleYear,
                    manufacturer,
                    model,
                    vehicleClass,
                    motor,
                    transmission,
                    emissions,
                    emissionRating,
                    smogRating,
                    fuelType,
                    combinedFuelEconomy,
                    combinedFuelEconomyGasEquivalent,
                    rechargeTime,
                    range,
                    maxSoc,
                    minSoc,
                    roundTripEfficiency,
                    loadThreshold,
                    chargerCapacity,
                    startSoc,
                    chargingHomeHourStart,
                    chargingHomeHourEnd,
                    workHourStart,
                    workHourEnd,
                    v2gHourStart,
                    v2gHourEnd,
                    chargeProgram,
                    chargePreference,
                    enableV2G,
                    enableTOU,
                    periodPrices,
                    weekdayScheduleTOU,
                    weekendScheduleTOU,
                    arbitrageBuyPrice,
                    arbitrageSellPrice,
                    region: provinceCodeMap[locationId],
                },
                {
                    electricityPrice,
                },
                houseLoadProfile
            );
            change(`${accessor}.evProfile`, results.evProfile);
            setSpecs(results);
            setIsLoading(false);
        }, 0);
    };
    const fuelTypeInfo = specs.fuelTypeInfo;
    const vehicleClassInfo = specs.vehicleClassInfo;
    const transmissionInfo = specs.transmissionInfo;
    const annualDistanceDriven = specs.annualDistanceDriven;
    const annualEmissions = specs.annualEmissions;
    const annualFuelConsumption = specs.annualFuelConsumption;
    const annualFuelCost = specs.annualFuelCost;
    const [reRenderKey, setReRenderKey] = useState(0);

    useEffect(() => {
        // setReRenderKey((prev) => prev + 1); // force re-render if year, model, or manufacturer change
    }, [vehicleYear, model, manufacturer]);

    // Change Event Handlers - specific to EVs
    const handleYearChange = () => {
        change(`${accessor}.manufacturer`, ""); // Reset manufacturer when year changes
        change(`${accessor}.model`, ""); // Reset model when year changes
        resetValues(change, accessor);
    };

    const handleManufacturerChange = (selectedManufacturer) => {
        change(`${accessor}.manufacturer`, selectedManufacturer);
        change(`${accessor}.model`, ""); // Reset model when manufacturer changes
        resetValues(change, accessor);
    };

    const handleModelChange = (selectedModel) => {
        change(`${accessor}.model`, selectedModel);

        const vehicleData = vehicleMap?.[vehicleYear]?.[manufacturer]?.[selectedModel] || {};

        if (isEmpty(vehicleData)) {
            console.error("Vehicle data is not found"); // Handle the case where vehicleData is empty
        } else {
            change(`${accessor}.vehicleClass`, vehicleData.vehicleClass);
            change(`${accessor}.motor`, vehicleData.generalSpecifications.motor);
            change(`${accessor}.transmission`, vehicleData.generalSpecifications.transmission);
            change(`${accessor}.emissions`, vehicleData.generalSpecifications.emissions);
            change(`${accessor}.emissionRating`, vehicleData.generalSpecifications.emissionRating);
            change(`${accessor}.smogRating`, vehicleData.generalSpecifications.smogRating);
            change(`${accessor}.fuelType`, vehicleData.electricSpecifications.fuelType);
            change(`${accessor}.combinedFuelEconomy`, vehicleData.electricSpecifications.combinedFuelEconomy);
            change(
                `${accessor}.combinedFuelEconomyGasEquivalent`,
                vehicleData.electricSpecifications.combinedFuelEconomyGasEquivalent
            );
            change(`${accessor}.rechargeTime`, vehicleData.electricSpecifications.rechargeTime);
            change(`${accessor}.range`, vehicleData.electricSpecifications.range);
        }
    };

    const updateV2GAndTOUSettings = (chargeProgram, chargePreference) => {
        //Exit early if we're dealing with a "None" charge program where we can handle any combination
        if (chargeProgram === "None") return;

        const settingsMap = {
            None_Uncontrolled_N: { v2g: false, tou: false }, // no program, max-rate
            None_Uncontrolled_Y: { v2g: true, tou: false }, // no program max-rate + v2g

            "None_Load Leveling_N": { v2g: false, tou: false }, // no program, even-rate
            "None_Load Leveling_Y": { v2g: true, tou: false }, // no program, even-rate + v2g

            Arbitrage_Uncontrolled_Y: { v2g: true, tou: true }, // price-based, max-rate (requires v2g and tou)
            "Arbitrage_Load Leveling_Y": { v2g: true, tou: true }, // price-based, even-rate (requires v2g and tou)

            "Load-Based_Uncontrolled_N": { v2g: false, tou: false }, // load-based, max-rate
            "Load-Based_Uncontrolled_Y": { v2g: true, tou: false }, // load-based, max-rate + v2g
            "Load-Based_Load Leveling_N": { v2g: false, tou: false }, // load-based, even-rate
            "Load-Based_Load Leveling_Y": { v2g: true, tou: false }, // load-based, even-rate + v2g
            "None_Load Levelling_N": { v2g: false, tou: false },
            "None_Load Levelling_Y": { v2g: true, tou: false },
            "None_Load-Aware_N": { v2g: false, tou: false },
            "None_Load-Aware_Y": { v2g: true, tou: false },

            "Arbitrage_Load Levelling_Y": { v2g: true, tou: true },
            "Arbitrage_Load-Aware_Y": { v2g: true, tou: true },

            // BAD OPTIONS
            // "Arbitrage_Uncontrolled_N": { v2g: false, tou: true },
            // "Arbitrage_Load Levelling_N": { v2g: false, tou: true },
        };

        // Check both options for Y/N variants if needed
        let keyY = `${chargeProgram}_${chargePreference}_Y`;
        let keyN = `${chargeProgram}_${chargePreference}_N`;

        let settings = settingsMap[keyY] || settingsMap[keyN] || { v2g: false, tou: false };

        change(`${accessor}.enableV2G`, settings.v2g);
        change(`${accessor}.enableTOU`, settings.tou);
    };

    const handleChargeProgramChange = (selectedProgram) => {
        change(`${accessor}.chargeProgram`, selectedProgram);

        updateV2GAndTOUSettings(selectedProgram, chargePreference);
    };

    const handleChargePreferenceChange = (selectedPreference) => {
        change(`${accessor}.chargePreference`, selectedPreference);

        updateV2GAndTOUSettings(chargeProgram, selectedPreference);
    };

    // Label Creation
    const createLabel = () => {
        if (vehicleYear && manufacturer && !model) {
            return `${vehicleYear} ${manufacturer}`;
        } else if (vehicleYear && manufacturer && model) {
            return `${vehicleYear} ${manufacturer} ${model}`;
        } else {
            return `Electric | Vehicle ${sysNum}`;
        }
    };

    return (
        <Accordion
            className={sharedClasses.accordion}
            heading={<span className={classes.accordionTitle}>{createLabel()}</span>}
            large
            hoverActions={<Actions accessor={accessor} change={change} formName={formName} />}
            forceOpen={contentOpen || forceOpenProp}
        >
            <div key={reRenderKey}>
                <div data-transportation-tour="commute-distance">
                    <h4>Driving Behavior</h4>
                    <InputRow gridTemplate="1fr 1fr 1fr">
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.commuteDistance`}
                            label="Round-trip Daily Commute Distance"
                            placeholder={commuteDistance ? commuteDistance : "Input Daily Round-trip Commute Distance"}
                            validate={commuteDistanceValidation}
                            decimals={getDecimalPlaces("commuteDistance")}
                            change={change}
                            units={{
                                base: getBaseUnits("commuteDistance", modelUnits),
                                options: getUnitOptions("distance"),
                                selected: commuteDistanceUnits,
                                unitType: "distance",
                                accessor: `${accessor}.commuteDistance_u`,
                            }}
                            setValue={Math.min(Math.max(commuteDistance, 0), 1000)}
                        />
                    </InputRow>
                </div>

                <div data-transportation-tour="general-specifications">
                    <h4>General Specifications</h4>
                    <InputRow gridTemplate="1fr 1fr 1fr">
                        <Field
                            component={Select}
                            search={true}
                            name={`${accessor}.vehicleYear`}
                            options={yearOpts}
                            label="Year"
                            placeholder="Select Year"
                            onChange={handleYearChange}
                            setValue={vehicleYear}
                        />
                        <Field
                            component={Select}
                            search={true}
                            name={`${accessor}.manufacturer`}
                            options={manufacturerOpts}
                            label="Manufacturer"
                            placeholder="Select Manufacturer"
                            info={
                                '"Generic" provides pre-selected vehicle models that fall into the 25th (Low), 50th (Mid), and 75th (High) percentiles for their year, based on fuel economy (kWh/100km) and vehicle class.'
                            }
                            onChange={handleManufacturerChange}
                            setValue={manufacturer}
                            disabled={!vehicleYear}
                        />
                        <Field
                            component={Select}
                            search={true}
                            name={`${accessor}.model`}
                            options={modelOpts}
                            label="Model"
                            placeholder="Select Model"
                            info={modelToolTip(model)}
                            onChange={handleModelChange}
                            setValue={model}
                            disabled={!vehicleYear || !manufacturer}
                        />
                    </InputRow>
                </div>

                <div data-transportation-tour="vehicle-specifications">
                    <h4>Vehicle Specifications</h4>
                    <InputRow gridTemplate="1fr 1fr 1fr">
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.motor`}
                            label="Motor"
                            validate={motorValidation}
                            decimals={getDecimalPlaces("motor")}
                            change={change}
                            units={{
                                base: getBaseUnits("motor", modelUnits),
                                options: getUnitOptions("motor"),
                                selected: motorUnits,
                                unitType: "motor",
                                accessor: `${accessor}.motor_u`,
                            }}
                            setValue={motor}
                            disabled={true}
                        />
                        <Field
                            component={Input}
                            name={`${accessor}.transmission`}
                            label="Transmission"
                            info={transmissionInfo}
                            change={change}
                            setValue={transmission}
                            disabled={true}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.emissions`}
                            label="CO₂ Emissions Rate"
                            validate={emissionsValidation}
                            decimals={getDecimalPlaces("emissionsRate")}
                            change={change}
                            units={{
                                base: getBaseUnits("emissionsRate", modelUnits),
                                options: getUnitOptions("emissionsRate"),
                                selected: emissionsRateUnits,
                                unitType: "emissionsRate",
                                accessor: `${accessor}.emissionsRate_u`,
                            }}
                            setValue={emissions === null ? "N/A" : emissions} // no emissions rating then set to N/A
                            disabled={true}
                        />
                        <Field
                            component={Input}
                            name={`${accessor}.emissionRating`}
                            label="CO₂ Rating"
                            info="Rated on a scale from 1 (worst) to 10 (best)"
                            change={change}
                            setValue={emissionRating === null ? "N/A" : emissionRating}
                            disabled={true}
                        />
                        <Field
                            component={Input}
                            name={`${accessor}.smogRating`}
                            label="Smog Rating"
                            info="Rated on a scale from 1 (worst) to 10 (best)"
                            change={change}
                            setValue={smogRating === null ? "N/A" : smogRating}
                            disabled={true}
                        />
                        <Field
                            component={Input}
                            name={`${accessor}.vehicleClass`}
                            label="Vehicle Class"
                            info={vehicleClassInfo}
                            change={change}
                            setValue={vehicleClass}
                            disabled={true}
                        />
                        <Field
                            component={Input}
                            name={`${accessor}.fuelType`}
                            label="Fuel Type"
                            change={change}
                            setValue={fuelTypeInfo}
                            disabled={true}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.combinedFuelEconomy`}
                            label="Combined Fuel Economy"
                            validate={combinedFuelEconomyValidation}
                            decimals={getDecimalPlaces("electricFuelEconomy")}
                            info="Value provided by NRCan reflects 55% city driving and 45% highway driving."
                            change={change}
                            units={{
                                base: getBaseUnits("electricFuelEconomy", modelUnits),
                                options: getUnitOptions("electricFuelEconomy"),
                                selected: electricFuelEconomyUnits,
                                unitType: "electricFuelEconomy",
                                accessor: `${accessor}.electricFuelEconomy_u`,
                            }}
                            setValue={combinedFuelEconomy}
                            disabled={true}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.combinedFuelEconomyGasEquivalent`}
                            label="Combined Fuel Economy (Gas Equivalent)"
                            validate={combinedFuelEconomyGasEquivalentValidation}
                            decimals={getDecimalPlaces("electricFuelEconomyGasEquivalent")}
                            info="Value provided by NRCan assumes 1 litre of gasoline contains the energy equivalent to 8.9 kWh of electricity."
                            change={change}
                            units={{
                                base: getBaseUnits("electricFuelEconomyGasEquivalent", modelUnits),
                                options: getUnitOptions("electricFuelEconomy"),
                                selected: electricFuelEconomyGasEquivalentUnits,
                                unitType: "electricFuelEconomy",
                                accessor: `${accessor}.electricFuelEconomyGasEquivalent_u`,
                            }}
                            setValue={combinedFuelEconomyGasEquivalent}
                            disabled={true}
                        />
                    </InputRow>
                </div>

                <div data-transportation-tour="vehicle-settings">
                    <h4>Vehicle Settings</h4>
                    <InputRow gridTemplate="1fr 1fr 1fr">
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.minSoc`}
                            label="Minimum SOC"
                            validate={stateOfChargeValidation}
                            decimals={getDecimalPlaces("stateOfCharge")}
                            info="The lowest battery level you allow your EV to reach before disallowing further discharge. Setting a minimum state of charge (SoC) ensures that your vehicle retains enough power for driving needs even when using vehicle-to-grid (V2G) services."
                            change={change}
                            units={{
                                base: getBaseUnits("stateOfCharge", modelUnits),
                                options: getUnitOptions("stateOfCharge"),
                                selected: stateOfChargeUnits,
                                unitType: "stateOfCharge",
                                accessor: `${accessor}.stateOfCharge_u`,
                            }}
                            warning={minSoc >= maxSoc ? "minSocHigherThanMaxSoc" : ""}
                            setValue={minSoc}
                            disabled={!vehicleYear || !manufacturer || !model}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.maxSoc`}
                            label="Maximum SOC"
                            validate={stateOfChargeValidation}
                            decimals={getDecimalPlaces("stateOfCharge")}
                            info="The highest battery level your EV will charge to. Setting a maximum state of charge (SoC) helps preserve battery life and optimize charging cycles while allowing flexibility for vehicle-to-grid (V2G) or time-of-use (TOU) programs."
                            change={change}
                            units={{
                                base: getBaseUnits("stateOfCharge", modelUnits),
                                options: getUnitOptions("stateOfCharge"),
                                selected: stateOfChargeUnits,
                                unitType: "stateOfCharge",
                                accessor: `${accessor}.stateOfCharge_u`,
                            }}
                            warning={maxSoc <= minSoc ? "maxSocLowerThanMinSoc" : ""}
                            setValue={maxSoc}
                            disabled={!vehicleYear || !manufacturer || !model}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.startSoc`}
                            label="Starting SOC"
                            validate={stateOfChargeValidation}
                            decimals={getDecimalPlaces("stateOfCharge")}
                            info="The battery level at the start of your charging session. This value helps monitor charging efficiency and estimate the time needed to reach your desired charge level."
                            change={change}
                            units={{
                                base: getBaseUnits("stateOfCharge", modelUnits),
                                options: getUnitOptions("stateOfCharge"),
                                selected: stateOfChargeUnits,
                                unitType: "stateOfCharge",
                                accessor: `${accessor}.stateOfCharge_u`,
                            }}
                            warning={startSoc > maxSoc || startSoc < minSoc ? "startSocOutsideOfRange" : ""}
                            setValue={startSoc}
                            disabled={!vehicleYear || !manufacturer || !model}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.chargerCapacity`}
                            label="Charger Capacity"
                            validate={chargerValidation}
                            decimals={getDecimalPlaces("chargerSettings")}
                            info="A typical Level 2 charger would draw 6-7.5 kW of power from the grid, while a Level 1 charger will draw less than 2.4 kW."
                            change={change}
                            units={{
                                base: getBaseUnits("chargerSettings", modelUnits),
                                options: getUnitOptions("chargerSettings"),
                                selected: chargerUnits,
                                unitType: "chargerSettings",
                                accessor: `${accessor}.charger_u`,
                            }}
                            setValue={chargerCapacity}
                            disabled={!vehicleYear || !manufacturer || !model}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.roundTripEfficiency`}
                            label="Round Trip Efficiency"
                            validate={roundTripEfficiencyValidation}
                            decimals={getDecimalPlaces("roundTripEfficiency")}
                            //info="A typical Level 2 charger would draw 6-7.5 kW of power from the grid, while a Level 1 charger will draw less than 2.4 kW."
                            change={change}
                            units={{
                                base: getBaseUnits("roundTripEfficiency", modelUnits),
                                options: getUnitOptions("roundTripEfficiency"),
                                selected: efficiencyUnits,
                                unitType: "roundTripEfficiency",
                                accessor: `${accessor}.roundTripEfficiency_u`,
                            }}
                            setValue={roundTripEfficiency}
                            disabled={!vehicleYear || !manufacturer || !model}
                        />
                    </InputRow>
                </div>
                <div data-transportation-tour="schedule-hours">
                    <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                        <Field
                            component={Input}
                            type="number"
                            decimals={0}
                            validate={time24hValidation}
                            name={`${accessor}.chargingHomeHourStart`}
                            label="Return Home Hour"
                            info="The time when home charging for your EV begins. Scheduling home charging can help you take advantage of off-peak electricity rates or renewable energy sources."
                            placeholder={"Enter Value"}
                            change={change}
                            setValue={chargingHomeHourStart}
                            disabled={!vehicleYear || !manufacturer || !model}
                        />
                        <Field
                            component={Input}
                            type="number"
                            decimals={0}
                            validate={time24hValidation}
                            name={`${accessor}.chargingHomeHourEnd`}
                            label="Depart Home Hour"
                            info="The time when home charging for your EV ends. This ensures your vehicle charges only during preferred times, possibly reducing costs by aligning with time-of-use (TOU) rates."
                            placeholder={"Enter Value"}
                            change={change}
                            setValue={chargingHomeHourEnd}
                            disabled={!vehicleYear || !manufacturer || !model}
                        />
                        <Field
                            component={Input}
                            type="number"
                            decimals={0}
                            validate={time24hValidation}
                            name={`${accessor}.workHourStart`}
                            label="Work Start Hour"
                            info="The time when your workday begins. This time frame helps tailor vehicle-to-grid (V2G) and charging schedules based on when your EV will be in use and when it may be available for grid services."
                            change={change}
                            setValue={workHourStart}
                            placeholder={"Enter Value"}
                            disabled={!vehicleYear || !manufacturer || !model}
                        />
                        <Field
                            component={Input}
                            type="number"
                            decimals={0}
                            validate={time24hValidation}
                            name={`${accessor}.workHourEnd`}
                            label="Work End Hour"
                            info="The time when your workday ends. This allows you to optimize charging or vehicle-to-grid (V2G) usage, ensuring your vehicle is ready for the commute home or for grid services during non-working hours."
                            placeholder={"Enter Value"}
                            setValue={workHourEnd}
                            disabled={!vehicleYear || !manufacturer || !model}
                        />
                    </InputRow>
                </div>

                <h4>Program Options</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <div data-transportation-tour="charge-program">
                        <Field
                            component={Select}
                            name={`${accessor}.chargeProgram`}
                            options={chargeProgramOpts}
                            label="Charge Program"
                            info="Gives the option of a pre-set schedule that determines when and how your EV charges, based on factors like energy pricing, availability of renewable energy, and vehicle-to-grid (V2G) participation. Arbitrage uses V2G and varying TOU rates to buy low-cost electricity and provide it to the grid when the sell price is higher than the buy price."
                            placeholder="Select Charge Program"
                            onChange={handleChargeProgramChange}
                            setValue={chargeProgram}
                            disabled={!vehicleYear || !manufacturer || !model || !commuteDistance}
                        />
                    </div>
                    <div data-transportation-tour="charge-preference">
                        <Field
                            component={Select}
                            name={`${accessor}.chargePreference`}
                            options={chargePreferenceOpts}
                            label="Charge Preference"
                            info="Your chosen charging strategy, which could prioritize fast charging, lowest cost, or minimal environmental impact. Adjusting your charge preference can help balance cost, convenience, and sustainability. Load levelling spreads charging evenly overnight between evening return and morning departure times."
                            onChange={handleChargePreferenceChange}
                            placeholder="Select Charge Preference"
                            setValue={chargePreference}
                            disabled={!vehicleYear || !manufacturer || !model || !commuteDistance}
                        />
                    </div>
                    {chargeProgram.toLowerCase() === "load-based" ? (
                        <div data-transportation-tour="charge-preference">
                            <Field
                                component={InputWithUnits}
                                name={`${accessor}.loadThreshold`}
                                type="number"
                                label="Load Threshold"
                                validate={loadThresholdValidation}
                                decimals={getDecimalPlaces("loadThreshold")}
                                //info=""
                                change={change}
                                units={{
                                    base: getBaseUnits("loadThreshold", modelUnits),
                                    options: getUnitOptions("loadThreshold"),
                                    selected: loadThresholdUnits,
                                    unitType: "loadThreshold",
                                    accessor: `${accessor}.loadThreshold_u`,
                                }}
                                setValue={loadThreshold}
                                disabled={!vehicleYear || !manufacturer || !model}
                            />
                        </div>
                    ) : null}
                </InputRow>

                <div data-transportation-tour="v2g-options">
                    <h4>
                        Vehicle-to-Grid Options
                        {((chargeProgram === "Arbitrage" && !enableTOU) ||
                            (chargeProgram === "Arbitrage" && !enableV2G)) && (
                            <span style={{ color: "#C21818", fontWeight: "bold" }}>
                                {" "}
                                - Warning! Invalid V2G Selection (Must select V2G and TOU).
                            </span>
                        )}
                    </h4>
                    <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                        <Field
                            component={Checkbox}
                            name={`${accessor}.enableV2G`}
                            label="Enable V2G"
                            info="Enabling V2G allows your EV to discharge energy back to the grid, helping balance electricity demand. This can reduce your total load during peak hours but may limit your driving range depending on your minimum state of charge (SoC) setting."
                            large
                            className={classes.bottomCheckBox}
                            type="checkbox"
                            disabled={!vehicleYear || !manufacturer || !model || !commuteDistance}
                        />
                        <Field
                            component={Input}
                            type="number"
                            decimals={0}
                            validate={time24hValidation}
                            name={`${accessor}.v2gHourStart`}
                            label="Start V2G Hour"
                            placeholder={"Enter Value"}
                            setValue={v2gHourStart}
                            warning={
                                (
                                    chargingHomeHourStart < chargingHomeHourEnd
                                        ? v2gHourStart < chargingHomeHourStart || v2gHourStart >= chargingHomeHourEnd
                                        : v2gHourStart < chargingHomeHourStart && v2gHourStart >= chargingHomeHourEnd
                                )
                                    ? "v2gStartHourOutsideOfRange"
                                    : ""
                            }
                            disabled={!enableV2G}
                        />
                        <Field
                            component={Input}
                            type="number"
                            decimals={0}
                            validate={time24hValidation}
                            name={`${accessor}.v2gHourEnd`}
                            label="End V2G Hour"
                            placeholder={"Enter Value"}
                            setValue={v2gHourEnd}
                            warning={
                                (
                                    chargingHomeHourStart < chargingHomeHourEnd
                                        ? v2gHourEnd <= chargingHomeHourStart || v2gHourEnd > chargingHomeHourEnd
                                        : v2gHourEnd <= chargingHomeHourStart && v2gHourEnd > chargingHomeHourEnd
                                )
                                    ? "v2gEndHourOutsideOfRange"
                                    : ""
                            }
                            disabled={!enableV2G}
                        />

                        {chargeProgram === "Arbitrage" ? (
                            <>
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`${accessor}.arbitrageBuyPrice`}
                                    label="Buy Price"
                                    info={"Highest price that electricity is bought for"}
                                    decimals={getDecimalPlaces("timeOfUsePrice")}
                                    change={change}
                                    units={{
                                        base: getBaseUnits("timeOfUsePrice", modelUnits),
                                        options: getUnitOptions("TOUptimeOfUsePrice"),
                                        selected: timeOfUsePriceUnits,
                                        unitType: "timeOfUsePrice",
                                        accessor: `${accessor}.timeOfUsePrice_u`,
                                    }}
                                    placeholder={"0.00"}
                                    warning={
                                        enableV2G &&
                                        arbitrageBuyPrice < Math.min(...periodPrices.concat(electricityPrice * 100))
                                            ? "incorrectArbitrageBuyPrice"
                                            : ""
                                    }
                                    setValue={arbitrageBuyPrice}
                                    disabled={!enableV2G}
                                />
                                <Field
                                    component={InputWithUnits}
                                    type="number"
                                    name={`${accessor}.arbitrageSellPrice`}
                                    label="Sell Price"
                                    info={"Lowest price that electricity is sold for"}
                                    decimals={getDecimalPlaces("timeOfUsePrice")}
                                    change={change}
                                    units={{
                                        base: getBaseUnits("timeOfUsePrice", modelUnits),
                                        options: getUnitOptions("timeOfUsePrice"),
                                        selected: timeOfUsePriceUnits,
                                        unitType: "timeOfUsePrice",
                                        accessor: `${accessor}.timeOfUsePrice_u`,
                                    }}
                                    placeholder={"0.00"}
                                    warning={
                                        enableV2G &&
                                        arbitrageSellPrice > Math.max(...periodPrices.concat(electricityPrice * 100))
                                            ? "incorrectArbitrageSellPrice"
                                            : ""
                                    }
                                    setValue={arbitrageSellPrice}
                                    disabled={!enableV2G || !arbitrageBuyPrice}
                                />
                            </>
                        ) : null}
                    </InputRow>
                </div>

                <div data-transportation-tour="tou-options">
                    <h4>
                        Time-of-Use Options
                        {enableTOU && (containsZero(weekdayScheduleTOU) || containsZero(weekendScheduleTOU)) && (
                            <span style={{ color: "#C21818", fontWeight: "bold" }}>
                                {" "}
                                - Warning! Invalid TOU Schedule (General Tab).
                            </span>
                        )}
                    </h4>
                    <InputRow gridTemplate="1fr 1fr 1fr">
                        <Field
                            component={Checkbox}
                            name={`${accessor}.enableTOU`}
                            label="Enable TOU Cost Calculations"
                            info="Enabling TOU adjusts your charging behavior based on electricity pricing throughout the day. By charging during off-peak hours and reducing demand during high-cost periods, TOU can help lower your total energy load and save on electricity costs."
                            large
                            className={classes.bottomCheckBox}
                            type="checkbox"
                            style={{
                                paddingTop: "25px",
                            }}
                            disabled={!vehicleYear || !manufacturer || !model || !commuteDistance}
                        />
                    </InputRow>
                </div>

                {/*<InputRow gridTemplate="1fr">
                    <Button
                        type="button"
                        className={`${classes.resultButton}`} //${sharedClasses.buttonClass}`}
                        onClick={handleButton}
                        disabled={
                            !commuteDistance ||
                            (enableTOU && (containsZero(weekdayScheduleTOU) || containsZero(weekendScheduleTOU)))
                        }
                    >
                        Calculate Results
                    </Button>
                </InputRow>

                <h4>Results</h4>
                <InputRow gridTemplate="1fr 1fr 1fr">
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualDistanceDriven`}
                        label="Annual Distance Driven"
                        decimals={getDecimalPlaces("distance")}
                        change={change}
                        units={{
                            base: getBaseUnits("distance", modelUnits),
                            options: getUnitOptions("distance"),
                            selected: distanceUnits,
                            unitType: "distance",
                            accessor: `${accessor}.distance_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualDistanceDriven}
                        disabled={true}
                        info={"Assuming only daily commute driving, 5 days a week, 50 weeks a year."}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualEmissions`}
                        label="Annual Emissions"
                        decimals={getDecimalPlaces("emissions")}
                        change={change}
                        units={{
                            base: getBaseUnits("emissions", modelUnits),
                            options: getUnitOptions("emissions"),
                            selected: emissionsUnits,
                            unitType: "emissions",
                            accessor: `${accessor}.emissions_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualEmissions}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualFuelConsumption`}
                        label="Annual Fuel Consumption"
                        decimals={getDecimalPlaces("electricityConsumption")}
                        change={change}
                        units={{
                            base: getBaseUnits("electricityConsumption", modelUnits),
                            options: getUnitOptions("electricityConsumption"),
                            selected: electricityConsumptionUnits,
                            unitType: "electricityConsumption",
                            accessor: `${accessor}.electricityConsumption_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualFuelConsumption}
                        disabled={true}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.annualFuelCost`}
                        label="Annual Fuel Cost"
                        decimals={getDecimalPlaces("price")}
                        change={change}
                        units={{
                            base: getBaseUnits("price", modelUnits),
                            options: getUnitOptions("price"),
                            selected: priceUnits,
                            unitType: "price",
                            accessor: `${accessor}.price_u`,
                        }}
                        placeholder={"0.00"}
                        setValue={annualFuelCost}
                        disabled={true}
                    />
                </InputRow>*/}
            </div>
        </Accordion>
    );
};
